import React, { useState, useEffect } from 'react';
import {
  Button as MuiButton, Box, CircularProgress, Container,
  Grid,
} from '@mui/material';
import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import { useParams, useNavigate } from 'react-router-dom';
import { useRollbar } from '@rollbar/react';
import DOMPurify from 'dompurify';
import Toast, { useToast } from '../../components/useToast';
import {
  axiosIntakeInstance,
  axiosCustomerInstance,
  axiosErrorToUserMessage,
} from '../../services/axios';
import ZoomableImage from '../../components/ZoomableImage';
import useSiteSettings from '../../hooks/useSiteSettings';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import BottomNavBar from '../Appbar/BottomNavBar';
import HtmlTextEntry from '../../components/HtmlTextEntry';
import OfferedTemplates from './IntakeReviewPage/OfferedTemplates';
import IntakeReviewPageHeader from './IntakeReviewPageHeader';
import Signature from './Signature';
import ScribeSessionPanel from './ScribeSession';

export default function IntakeReviewPage() {
  const [intakeContent, setIntakeContent] = useState('');
  const [baseReportHtml, setBaseReportHtml] = useState('');
  const [attachedImages, setAttachedImages] = useState([]);
  const [templateOptions, setTemplateOptions] = useState({});
  const [showImages, setShowImages] = useState(false);
  const [canEditReport, setCanEditReport] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [patientSupportEmailRecipient, setPatientSupportEmailRecipient] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isReportLoaded, setIsReportLoaded] = useState(false);
  const [isSettingsLoaded, setIsSettingsLoaded] = useState(false);
  const [patientInfo, setPatientInfo] = useState({
    patientSex: '',
    patientAge: '',
    patientID: '',
  });
  const [headerLogo, setHeaderLogo] = useState('/assets/images/FirstHx_symbol_colour.jpg');
  const [signature, setSignature] = useState({ signatureImageSrc: '', signatureQuestion: '' });
  const [shouldPrintCustomLogo, setShouldPrintCustomLogo] = useState(false);
  const [isMedicationPdfEnabled, setIsMedicationPdfEnabled] = useState(false);
  const [isScribeEnabled, setIsScribeEnabled] = useState(false);
  const [showScribe, setShowScribe] = useState(false);
  const [scribeNoteVerbosity, setScribeNoteVerbosity] = useState('moderate');
  const [scribeNoteTemplate, setScribeNoteTemplate] = useState('');

  const navigate = useNavigate();

  const { mkey } = useParams();
  const { activeSiteId, accessibleSiteInfos } = useSiteSettings();
  const { rollbar } = useRollbar();

  const activeSiteName = accessibleSiteInfos.find((site) => site.id === activeSiteId)?.name || '';
  /* apiClient gives 401 auth error on page refresh. temp solution is to directly call
    useAxiosPrivate to bypass that error. update fn again once we figure out the bug */
  const intakeAxiosWithAuth = useAxiosPrivate(axiosIntakeInstance);
  const customerAxiosWithAuth = useAxiosPrivate(axiosCustomerInstance);

  const {
    toastState,
    toastSetSuccessNotification,
    toastSetErrorNotification,
    toastOnClose,
  } = useToast();

  const formatIntakeContentDisplay = (content) => {
    if (typeof content !== 'string') {
      rollbar.warn('"Invalid content" displayed', { mkey, content });
      return 'Invalid content';
    }

    if (content.trim() === '') {
      rollbar.info('"No content available" displayed', { mkey, content });
      return 'No content available';
    }

    return content;
  };

  const extractPiiValue = (data, pattern) => {
    const regex = new RegExp(`${pattern}: (.*?)\\n`, 's');
    const match = data.match(regex);
    return match && match[1] ? match[1].trim() : null;
  };

  const fetchData = async () => {
    try {
      const response = await intakeAxiosWithAuth.get(`/${activeSiteId}/${mkey}/review`);
      const sex = extractPiiValue(response.data.report, 'Sex');
      const age = extractPiiValue(response.data.report, 'Age');
      const id = extractPiiValue(response.data.report, 'Patient ID');
      setPatientInfo((prevState) => ({
        ...prevState,
        patientSex: sex,
        patientAge: age,
        patientID: id,
      }));

      const formattedIntakeHtmlReport = formatIntakeContentDisplay(
        DOMPurify.sanitize(response.data.reportHtml),
      );
      setBaseReportHtml(formattedIntakeHtmlReport);
      setAttachedImages(response.data.attachedFilesWithAccessToken?.map((file, index) => (
        { ...file, zoom: 1, index }
      )));
      setTemplateOptions(response.data.templatesToOffer);
      setSignature(response.data.signature);
    } catch (error) {
      toastSetErrorNotification(
        axiosErrorToUserMessage(error)
                || 'An error occurred when fetching the report.',
      );
    }
  };

  const fetchAndSetSiteConfigurations = async () => {
    try {
      const response = await customerAxiosWithAuth.get(`/${activeSiteId}`);
      const settings = response?.data?.settings || [];
      const canEditReportSetting = settings.find((setting) => setting.name === 'canEditReport');
      const clientEmailSetting = settings.find((setting) => setting.name === 'patientSupportEmailRecipient');
      setPatientSupportEmailRecipient(clientEmailSetting?.value);
      const reportEditable = canEditReportSetting ? canEditReportSetting.value === 'true' : false;
      setCanEditReport(reportEditable);

      const logoSetting = settings.find((setting) => setting.name === 'consentPageLogo');
      // Check if consentPageLogo exists and is not the default logo
      const hasCustomLogo = logoSetting && logoSetting.value !== '/FirstHx_logo.png';
      setShouldPrintCustomLogo(hasCustomLogo);

      if (logoSetting) {
        setHeaderLogo(logoSetting.value);
      }
      const medicationPdfSetting = settings.find((setting) => setting.name === 'generateMedsCheckPmrPdf');
      setIsMedicationPdfEnabled(medicationPdfSetting?.value === 'true');
      const scribeSetting = settings.find((setting) => setting.name === 'scribeEnabled');
      setIsScribeEnabled(scribeSetting?.value === 'true');
      const verbositySetting = settings.find((setting) => setting.name === 'scribeNoteVerbosity');
      if (verbositySetting?.value) setScribeNoteVerbosity(verbositySetting.value);
      const templateSetting = settings.find((setting) => setting.name === 'scribeNoteTemplate');
      if (templateSetting?.value !== undefined) setScribeNoteTemplate(templateSetting.value);
    } catch (error) {
      toastSetErrorNotification(
        axiosErrorToUserMessage(error)
                || 'An error occurred when fetching site info',
      );
    }
  };

  useEffect(() => {
    if (activeSiteId && mkey) {
      setIsLoading(true);
      setIsReportLoaded(false);
      setIsSettingsLoaded(false);
      fetchData().finally(() => setIsReportLoaded(true));
      fetchAndSetSiteConfigurations().finally(() => setIsSettingsLoaded(true));
    }
  }, [activeSiteId, mkey]);

  useEffect(() => {
    const run = async () => {
      if (!isReportLoaded || !isSettingsLoaded) return;
      setIntakeContent(baseReportHtml);
      setIsLoading(false);
    };
    run();
  }, [
    isReportLoaded,
    isSettingsLoaded,
    baseReportHtml,
  ]);

  const handleBack = () => {
    navigate(-1); // go back one page
  };

  const updateZoom = (index, newZoom) => {
    setAttachedImages((currentImages) => currentImages.map((img, idx) => (idx === index
      ? { ...img, zoom: newZoom } : img)));
  };

  const renderImages = () => showImages && (
    <Box
      sx={{
        justifyContent: 'center',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 2,
        maxWidth: '100%',
        overflow: 'auto',
      }}
    >
      {attachedImages?.map((image, index) => (
        <ZoomableImage
          key={`attachedImage_${image.urlWithAccessToken}`}
          image={image}
          onZoomChange={(newZoom) => updateZoom(index, newZoom)}
        />
      ))}
    </Box>
  );

  const handleSaveReport = async () => {
    try {
      setIsSaving(true);
      await intakeAxiosWithAuth.put(`/${mkey}/report/save`, {
        reportText: intakeContent,
      });
      toastSetSuccessNotification('Saved');
    } catch (error) {
      toastSetErrorNotification(
        axiosErrorToUserMessage(error) || 'An error occurred while saving the report.',
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <IntakeReviewPageHeader
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...patientInfo}
        onBack={handleBack}
        headerLogo={headerLogo}
      />
      <Container>
        <Box sx={{ padding: '30px', paddingTop: '80px' }}>
          {isLoading ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 'calc(100vh)',
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              <MuiButton
                variant="text"
                sx={{
                  color: 'black',
                  display: 'flex',
                  justifyContent: 'flex-start',
                  mb: 2,
                }}
                onClick={handleBack}
                startIcon={<KeyboardBackspaceRoundedIcon />}
              >
                HOME
              </MuiButton>
              {isScribeEnabled && (
                <Box sx={{ mb: 2, textAlign: 'center' }}>
                  <MuiButton variant="outlined" onClick={() => setShowScribe(!showScribe)}>
                    {showScribe ? 'Hide Scribe' : 'Scribe'}
                  </MuiButton>
                </Box>
              )}
              <Grid container spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={showScribe && isScribeEnabled ? 8 : 12}
                  sx={{ order: { xs: 1, md: 0 } }}
                >
                  <HtmlTextEntry
                    value={intakeContent}
                    onChange={(val) => {
                      setIntakeContent(val);
                    }}
                    minRows={5}
                    readonly={!canEditReport}
                    onSave={handleSaveReport}
                    onSuccess={toastSetSuccessNotification}
                    isSaving={isSaving}
                    patientID={patientInfo.patientID}
                    mkey={mkey}
                    activeSiteId={activeSiteId}
                    customLogoPath={shouldPrintCustomLogo ? headerLogo : null}
                    siteName={shouldPrintCustomLogo ? activeSiteName : null}
                    isMedicationPdfEnabled={isMedicationPdfEnabled}
                  />
                  {canEditReport && templateOptions && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <OfferedTemplates
                        examTemplates={templateOptions.exam}
                        planTemplates={templateOptions.plan}
                        intakeContent={intakeContent}
                        setIntakeContent={setIntakeContent}
                      />
                    </Box>
                  )}
                  {signature?.signatureImageSrc
                    && <Signature signature={signature} patientID={patientInfo.patientID} />}
                </Grid>
                {isScribeEnabled && showScribe && (
                  <Grid item xs={12} md={4} sx={{ order: { xs: 0, md: 1 } }}>
                    <ScribeSessionPanel
                      siteId={activeSiteId}
                      mkey={mkey}
                      intakeReport={intakeContent}
                      verbosity={scribeNoteVerbosity}
                      noteTemplate={scribeNoteTemplate}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sx={{ textAlign: 'center', order: 2 }}>
                  {attachedImages?.length > 0 && (
                  <MuiButton sx={{ mt: 2 }} onClick={() => setShowImages(!showImages)}>
                    {showImages ? 'Hide Images' : `Show Images (${attachedImages.length})`}
                  </MuiButton>
                  )}
                  {renderImages()}
                </Grid>
              </Grid>
            </>
          )}
        </Box>
        {patientSupportEmailRecipient ? (
          <BottomNavBar
            patientSupportEmailRecipient={patientSupportEmailRecipient}
            activeSiteId={activeSiteId}
          />
        ) : null}
        <Toast
          open={toastState.open}
          severity={toastState.severity}
          text={toastState.text}
          onClose={toastOnClose}
        />
      </Container>
    </>
  );
}
